// Transition message component

import React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useAuth, useRoomConfig } from '@/hooks';
import type { TransitionMessageProps } from './types';
import { FUNNY_EDGY, FUNNY_SAFE } from './configs';
import { interpolateMessage, shuffleMessages } from './utils';
import './TransitionMessage.css';

const TransitionMessage: React.FC<TransitionMessageProps> = ({
  messages,
  showSpinner = false,
  enableFunnyMessages = false,
  bottomEmphasized = false,
  interpolations = {},
  layout = 'overlay',
}) => {
  const { displayAccent } = useAuth();
  const { contentStyle } = useRoomConfig();
  const funnyConfig = contentStyle === 'edgy' ? FUNNY_EDGY : FUNNY_SAFE;
  const funnyMessages = React.useMemo(
    () =>
      Object.values(funnyConfig)
        .map((msgMap) => msgMap[displayAccent] || msgMap['en-US'])
        .filter(Boolean),
    [displayAccent, funnyConfig]
  );
  const [funnyMessage, setFunnyMessage] = React.useState('');

  React.useEffect(() => {
    if (!enableFunnyMessages || funnyMessages.length === 0) {
      setFunnyMessage('');
      return;
    }

    const shuffled = shuffleMessages(funnyMessages);
    let index = 0;
    setFunnyMessage(shuffled[index]);
    const timer = setInterval(() => {
      index = (index + 1) % shuffled.length;
      setFunnyMessage(shuffled[index]);
    }, 3000);

    return () => clearInterval(timer);
  }, [enableFunnyMessages, funnyMessages]);

  const top = interpolateMessage(messages?.top, displayAccent, interpolations);
  const bottom = interpolateMessage(
    messages?.bottom,
    displayAccent,
    interpolations
  );

  return (
    <div
      className={
        layout === 'overlay'
          ? 'absolute inset-0 flex flex-col items-center justify-center gap-6 p-6 text-center'
          : 'flex w-full flex-col items-center justify-center gap-6 text-center'
      }
    >
      {top && <div className="text-2xl font-normal text-foreground">{top}</div>}
      {showSpinner && <Spinner className="size-12 text-primary" />}

      {enableFunnyMessages && funnyMessage ? (
        <div
          key={funnyMessage}
          className="transition-message-funny text-xl text-muted-foreground"
          aria-live="polite"
        >
          {funnyMessage}
        </div>
      ) : bottom ? (
        <div
          className={
            bottomEmphasized
              ? 'text-2xl font-bold text-foreground'
              : 'text-xl text-muted-foreground'
          }
        >
          {bottom}
        </div>
      ) : null}
    </div>
  );
};

export default TransitionMessage;
